// src/pages/PlanPurchase.tsx
import React from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";
import { getAffCode } from "utils/aff";
import { useMe } from "../contexts/MeContext";

type PublicPlan = {
  id: number | string;
  name: string;
  price: number; // 表示用（= point をマップ）
  point: number; // 購入時に消費するポイント
  date?: string;
  publishTime?: string;
  targetAmount?: number | string;
  stakeHint?: string | number;
  buyPt?: string;
  salesCount?: number;
  supplyCount?: number;
};

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE_URL ||
  (process as any).env?.REACT_APP_API_BASE_URL ||
  "";
const API_PUBLIC = API_BASE ? `${API_BASE}/api/public` : "/api/public";
const API_ROOT = API_BASE ? `${API_BASE}/api` : "/api";

// 数値系のゆるいパーサ
const toNumber = (v: any): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v.replace(/[^\d]/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

// 数値＋単位の共通フォーマッタ
const formatNumberWithUnit = (
  value: any,
  unit: string,
  useComma: boolean = false
): string => {
  if (value === null || value === undefined || value === "") return "—";
  const n = toNumber(value);
  const base = useComma ? n.toLocaleString() : String(n);
  return `${base}${unit}`;
};

export default function PlanPurchase() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const loc = useLocation();
  const { me } = useMe();

  const [plan, setPlan] = React.useState<PublicPlan | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState<{ orderId: string } | null>(null);

  // 旧ロジック：URL / localStorage から拾う userId（保険用）
  const legacyUserId = React.useMemo(() => {
    const qs = new URLSearchParams(loc.search || "");
    return localStorage.getItem("okuman_user_id") || qs.get("uid") || "";
  }, [loc.search]);

  // 最終的に使う userId（MeContext 優先＋旧値のフォールバック）
  const userId =
    (me as any)?.userId || (me as any)?.id || legacyUserId || "";

  // サーバー互換用：name / email を必ず埋める
  const safeName =
    (me as any)?.lineName || (me as any)?.name || "BOAT億MAN会員";

  const safeEmail = (() => {
    const raw = (me as any)?.email;
    if (raw && typeof raw === "string" && raw.includes("@")) {
      return raw;
    }
    const base = userId || "user";
    return `${base}@no-email.boat-okuman.com`;
  })();

  const userPoint = me?.points ?? 0;
  const pointNeeded = plan?.point ?? 0;
  const lackPoint = !!me && userPoint < pointNeeded;

  // affiliateId をコードから解決（あるときだけ）
  const [affiliateId, setAffiliateId] = React.useState<string | null>(null);
  React.useEffect(() => {
    (async () => {
      try {
        const code = getAffCode();
        if (!code) return;
        const r = await fetch(
          `${API_PUBLIC}/affiliate/resolve?code=${encodeURIComponent(code)}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );
        const j = await r.json();
        if (j?.ok && j.affiliateId) setAffiliateId(String(j.affiliateId));
      } catch {
        // 失敗しても購入は続行可
      }
    })();
  }, []);

  // プランの取得（API：/api/public/plan/:id）
  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(`${API_PUBLIC}/plan/${id}`, {
          cache: "no-store",
          credentials: "include",
        });
        const p = await res.json();

        if (!res.ok || !p) {
          throw new Error("プランが見つかりませんでした。");
        }

        const point = Number(p.point) || 0;
        setPlan({
          id: p.id,
          name: String(p.name || ""),
          price: point,
          point,
          date: p.date || p.publishTime,
          publishTime: p.publishTime,
          targetAmount: p.targetAmount,
          stakeHint: p.stakeHint || p.stake || "",
          buyPt: p.buyPt || "",
          salesCount:
            typeof p.salesCount === "number" ? p.salesCount : undefined,
          supplyCount:
            typeof p.supplyCount === "number" ? p.supplyCount : undefined,
        });
      } catch (e: any) {
        setError(e?.message || "プランが見つかりませんでした。");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!plan) return;

    if (!userId) {
      alert("ログインが必要です。LINEログイン後に再度お試しください。");
      return;
    }
    if (!safeEmail) {
      alert("メール情報の取得に失敗しました。再ログインしてお試しください。");
      return;
    }
    if (lackPoint) {
      alert("ポイントが不足しています。ポイントをチャージしてください。");
      return;
    }

    try {
      setSubmitting(true);

      const body = {
        userId: String(userId),
        planId: plan.id,
        pointSpent: plan.point,
        priceJPY: 0,
        affiliateId: affiliateId,
        name: safeName,
        email: safeEmail,
      };

      const res = await fetch(`${API_ROOT}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const j = await res.json();

      if (!res.ok) {
        throw new Error(j?.error || `注文に失敗しました（HTTP ${res.status}）`);
      }

      const orderId = j.order?.id ?? j.orderId ?? j.id ?? "";
      setDone({ orderId: String(orderId) || "(no-id)" });
    } catch (err: any) {
      alert(err?.message ?? "注文に失敗しました");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading)
    return (
      <main className="lp">
        <Header />
        <div style={{ padding: 24 }}>読み込み中…</div>
        <Footer />
      </main>
    );

  if (error || !plan)
    return (
      <main className="lp">
        <Header />
        <div style={{ padding: 24, color: "crimson" }}>{error ?? "エラー"}</div>
        <Footer />
      </main>
    );

  if (done) {
    return (
      <main className="lp">
        <Header />
        <div
          style={{
            maxWidth: 560,
            margin: "40px auto",
            padding: "0 16px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 2px 10px rgba(0,0,0,.06)",
              padding: 24,
            }}
          >
            <h2>ご購入ありがとうございました</h2>
            <p style={{ marginTop: 8, marginBottom: 16 }}>
              買い目はマイページから公開時間にご確認いただけます
            </p>
            <p>
              注文番号：<strong>{done.orderId}</strong>
            </p>
            <p>プラン名：{plan.name}</p>
            <p>
              使用ポイント：
              <strong>{plan.point.toLocaleString()} pt</strong>
            </p>
            <div style={{ marginTop: 16 }}>
              <Link to="/mypage">マイページへ移動する</Link>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  // 表示用ラベルたち
  const supplyLabel = (() => {
    const src =
      plan.supplyCount != null
        ? plan.supplyCount
        : plan.salesCount != null
        ? plan.salesCount
        : null;
    return formatNumberWithUnit(src, " 点", false);
  })();

  const targetAmountLabel = formatNumberWithUnit(
    plan.targetAmount,
    " 円",
    true
  );

  const stakeLabel = formatNumberWithUnit(plan.stakeHint, " 円", true);

  const pointLabel = formatNumberWithUnit(plan.point, " pt", false);

  return (
    <main className="lp">
      <Header />
      <div
        style={{
          background: "#e5f1fb",
          minHeight: "100vh",
          padding: "24px 8px 40px",
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            background: "#f3f7fc",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
            padding: 20,
          }}
        >
          {/* タイトル帯 */}
          <div
            style={{
              background: "#1e2d50ff",
              color: "#fff",
              textAlign: "center",
              padding: "12px 8px",
              borderRadius: 8,
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: ".08em",
              marginBottom: 16,
            }}
          >
            商品のご購入手続き
          </div>

          {/* ===== ステップ01 提供情報 ===== */}
          <section
            style={{
              background: "#ffffff",
              borderRadius: 10,
              border: "1px solid #d0e3f6",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
              marginBottom: 18,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                background: "#e0f2fe",
                borderBottom: "1px solid #d0e3f6",
                fontWeight: 700,
                color: "#0f172a",
                fontSize: 14,
              }}
            >
              <span style={{ fontSize: 18 }}>🛒</span>
              <span>ステップ 01 提供情報</span>
            </div>

            <div>
              <div
                style={{
                  padding: "10px 16px",
                  background: "#1e2d50ff",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                提供情報
              </div>
              <div style={{ padding: 16 }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 14,
                  }}
                >
                  <tbody>
                    <tr>
                      <th style={tdHead}>プラン名</th>
                      <td style={tdBody}>{plan.name}</td>
                    </tr>
                    <tr>
                      <th style={tdHead}>提供数</th>
                      <td style={tdBody}>{supplyLabel}</td>
                    </tr>
                    <tr>
                      <th style={tdHead}>開催日</th>
                      <td style={tdBody}>
                        {plan.date || plan.publishTime || "—"}
                      </td>
                    </tr>
                    <tr>
                      <th style={tdHead}>目標金額</th>
                      <td style={tdBody}>{targetAmountLabel}</td>
                    </tr>
                    <tr>
                      <th style={tdHead}>推奨賭け金</th>
                      <td style={tdBody}>{stakeLabel}</td>
                    </tr>
                    <tr>
                      <th style={tdHead}>購入特典</th>
                      <td style={tdBody}>{plan.buyPt || "—"}</td>
                    </tr>
                    <tr>
                      <th style={tdHead}>ポイント</th>
                      <td style={tdBody}>{pointLabel}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* ===== ステップ02 ポイント決済 ===== */}
          <section
            style={{
              background: "#ffffff",
              borderRadius: 10,
              border: "1px solid #d0e3f6",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                background: "#e0f2fe",
                borderBottom: "1px solid #d0e3f6",
                fontWeight: 700,
                color: "#0f172a",
                fontSize: 14,
              }}
            >
              <span style={{ fontSize: 18 }}>💳</span>
              <span>ステップ 02 ポイント決済</span>
            </div>

            <div style={{ padding: 16 }}>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: 14,
                  color: "#0f172a",
                }}
              >
                ポイントを使ってプランを購入します。
              </p>

              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 12,
                  background: "#f8fafc",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                    fontSize: 14,
                  }}
                >
                  <span>所持ポイント</span>
                  <strong>{userPoint.toLocaleString()} pt</strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 14,
                  }}
                >
                  <span>利用ポイント</span>
                  <strong>{plan.point.toLocaleString()} pt</strong>
                </div>
              </div>

              {lackPoint && (
                <p
                  style={{
                    margin: "0 0 14px",
                    padding: "8px 10px",
                    background: "#fef2f2",
                    borderRadius: 6,
                    border: "1px solid #fecaca",
                    color: "#b91c1c",
                    fontSize: 13,
                  }}
                >
                  ご注意ください：ポイントが不足しています。ポイント購入ページからチャージしてから再度お申し込みください。
                </p>
              )}

              <form onSubmit={handleSubmit}>
                <button
                  type="submit"
                  disabled={submitting || lackPoint}
                  style={{
                    width: "100%",
                    height: 48,
                    border: "none",
                    borderRadius: 999,
                    background:
                      submitting || lackPoint ? "#94a3b8" : "#e11d48",
                    boxShadow: "0 4px 10px rgba(248, 113, 113, 0.45)",
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    cursor: submitting || lackPoint ? "default" : "pointer",
                    transition: "transform .05s ease-out",
                  }}
                >
                  {submitting ? "送信中…" : "購入を確定する"}
                </button>

                <div
                  style={{
                    textAlign: "center",
                    marginTop: 12,
                    fontSize: 13,
                  }}
                >
                  <Link to={`/plans/${plan.id}`}>← 詳細に戻る</Link>
                </div>
              </form>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </main>
  );
}

const tdHead: React.CSSProperties = {
  width: 140,
  padding: "8px 10px",
  background: "#f1f5f9",
  borderBottom: "1px solid #e2e8f0",
  fontWeight: 700,
};

const tdBody: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid #e2e8f0",
};
